import { DeleteOutlined, PhotoCameraOutlined } from "@material-ui/icons";
import { EditAboutLocalizationId } from "app/spaceStone/myProfile/localization";
import clsx from "clsx";
import React, { useMemo } from "react";
import { ALLOWED_AVATAR_TYPES } from "shared/constants/appConstants";
import { useLocalize } from "shared/hooks/useLocalize";
import { AvatarProps } from "shared/interfaces/avatar/avatar";
import { Avatar as MaterialAvatar, Tooltip, Typography } from "@material-ui/core";
import TextButton from "uiKit/buttons/TextButton";
import FileInput from "uiKit/inputs/fileInput";
import { useAvatarStyles } from "uiKit/labels/avatar/styles";
import { getAvatarDimensionsBySize } from "shared/helpers";
import { formatUserNameForAvatar } from "shared/helpers/formatUserName";

const Avatar: React.FC<AvatarProps> = ({
    firstName,
    lastName,
    src,
    size = "s",
    isEdit = false,
    handleDeleteButton,
    setImage,
    descriptionColumnDirection = false,
}: AvatarProps): JSX.Element => {
    const classes = useAvatarStyles(getAvatarDimensionsBySize(size));
    const localize = useLocalize();

    const typographyVariant = useMemo(() => {
        switch (size) {
            case "l":
                return "h1";
            case "s":
                return "h4";
            case "xxs":
                return "h6";
            default:
                return "h4";
        }
    }, [size]);

    const onFilesChange = (validFiles: File[]) => {
        setImage?.(validFiles[0]);
    };

    const avatarContent = !src ? (
        <Typography variant={typographyVariant} className={classes.avatarText}>
            {formatUserNameForAvatar(firstName, lastName)}
        </Typography>
    ) : (
        <img src={src} alt="" />
    );

    const avatarComponent = (
        <div>
            <FileInput onFilesChange={onFilesChange} accept={ALLOWED_AVATAR_TYPES}>
                {({ onInputClick }) => (
                    <MaterialAvatar className={classes.avatar}>
                        {!isEdit && avatarContent}
                        {isEdit && setImage && (
                            <Tooltip title={localize(EditAboutLocalizationId.EditAvatarTooltip)}>
                                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                                <div className={classes.avatarEditImage} onClick={onInputClick}>
                                    <>
                                        {avatarContent}
                                        <div className={classes.avatarEdit}>
                                            <PhotoCameraOutlined />
                                        </div>
                                    </>
                                </div>
                            </Tooltip>
                        )}
                    </MaterialAvatar>
                )}
            </FileInput>
        </div>
    );

    return (
        <>
            {isEdit ? (
                <div className={clsx(classes.avatarEditBox)}>
                    {avatarComponent}
                    <div
                        className={clsx(classes.descriptionBox, {
                            [classes.descriptionColumnDirection]: descriptionColumnDirection,
                        })}
                    >
                        <div className={classes.avatarDescription}>
                            <Typography variant="caption">
                                {localize(EditAboutLocalizationId.EditPhotoInformationMaxSize)}
                            </Typography>
                            <Typography variant="caption">
                                {localize(EditAboutLocalizationId.EditPhotoInformationAvailableFormats)}
                            </Typography>
                        </div>
                        {src && (
                            <TextButton
                                label={localize(EditAboutLocalizationId.EditDeletePhotoButton)}
                                startIcon={<DeleteOutlined />}
                                onClick={() => handleDeleteButton && handleDeleteButton()}
                                className={classes.avatarDeleteButton}
                            />
                        )}
                    </div>
                </div>
            ) : (
                avatarComponent
            )}
        </>
    );
};

export default Avatar;
